// Messages from the same sender this close together read as one burst.
export const GROUP_GAP_MS = 5 * 60 * 1000;

export interface ThreadMessageMeta {
  id: string;
  // Milliseconds; 0 marks a canned message (e.g. a greeting) with no time.
  time: number;
  // Who wrote it: consecutive messages with the same author group together.
  author: string;
  // A message that breaks the burst after it, e.g. a failed question.
  standalone?: boolean;
}

export type ThreadItem<T> =
  | { type: 'date'; key: string; date: number }
  | { type: 'message'; key: string; message: T; first: boolean; last: boolean };

const dayOf = (time: number) => new Date(time).toDateString();

// Lays messages (oldest first) out the Telegram way: a date chip when the day
// changes, and bubbles grouped so only the last of a burst gets the tail.
export const threadItems = <T>(messages: T[], meta: (message: T) => ThreadMessageMeta): ThreadItem<T>[] => {
  const items: ThreadItem<T>[] = [];
  const metas = messages.map(meta);
  const joins = (a?: ThreadMessageMeta, b?: ThreadMessageMeta) =>
    !!a &&
    !!b &&
    a.author === b.author &&
    !a.standalone &&
    dayOf(a.time) === dayOf(b.time) &&
    b.time - a.time < GROUP_GAP_MS;

  messages.forEach((message, index) => {
    const current = metas[index];
    const prev = metas[index - 1];
    const next = metas[index + 1];
    if (current.time && (!prev?.time || dayOf(prev.time) !== dayOf(current.time))) {
      items.push({ type: 'date', key: `date-${current.id}`, date: current.time });
    }
    items.push({
      type: 'message',
      key: current.id,
      message,
      first: !joins(prev, current),
      last: !joins(current, next),
    });
  });
  return items;
};
